import NoteHtml, { hasNoteContent } from '../../../../../components/common/NoteHtml';
import { resolveInterestNote } from './interestNote';
import { printConfigFor, vehicleSummary } from './invoiceSettingDefaults';
import { formatIstDate } from '../../../../../utils/helpers/istDate';

/**
 * Classic bill format (non-GST) modelled on the "Jai Ekdant" style maintenance
 * bill: a centered society header, a simple Sr.No / Particulars / Amount table,
 * a totals block (Total, Additional Previous Dues, Less Adjustments, Net Payable),
 * amount in words, a notes paragraph and a signature line — followed by a
 * dotted cut line and a payment RECEIPT counterfoil for the latest receipt.
 *
 * Consumes the same `invoice` shape as InvoicePrintPage.jsx.
 */
const InvoiceTemplateClassic = ({ invoice, pageIdx = 0, totalPages = 1, className = '' }) => {
    const config = printConfigFor(invoice);
    const vehicles = vehicleSummary(invoice, config);
    const society = invoice.society || {};
    const flat = invoice.flat || {};
    const member = invoice.member_details || {};
    const series = invoice.series || {};
    const items = invoice.items || [];

    // Society address (primary address falls back to first)
    const primaryAddress = society.addresses?.find(a => a.is_primary === 1) || society.addresses?.[0] || {};
    const societyAddress = [
        primaryAddress.address_line1,
        primaryAddress.address_line2,
        [primaryAddress.city?.name, primaryAddress.state?.name].filter(Boolean).join(', '),
        primaryAddress.pincode ? `- ${primaryAddress.pincode}` : ''
    ].filter(Boolean).join(', ');

    // Member entity details from ownership
    const ownership = flat.ownerships?.[0] || {};
    const entity = ownership.entity || {};

    // Separate charge items from system/tax items
    const SYSTEM_HEAD_CODES = ['CGST', 'SGST', 'IGST', 'PAR', 'DNR', 'INT', 'RND', 'ADJ'];
    const chargeItems = items.filter(item => !SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));
    const systemItems = items.filter(item => SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));
    const arrearItems = systemItems.filter(item => ['PAR', 'DNR', 'INT'].includes(item.tariffHead?.head_code));
    const taxItems = systemItems.filter(item => ['CGST', 'SGST', 'IGST'].includes(item.tariffHead?.head_code));

    const num = (v) => parseFloat(v || 0);
    const itemAmount = (item) => num(item.base_amount || item.system_amount);

    // Totals (invoice values are authoritative where available)
    const chargeSubtotal = chargeItems.reduce((sum, item) => sum + itemAmount(item), 0);
    const taxTotal = taxItems.reduce((sum, item) => sum + itemAmount(item), 0) || num(invoice.tax_amount);
    const currentInterest = num(invoice.interest_amount);
    const principalArrear = arrearItems
        .filter(i => ['PAR', 'DNR'].includes(i.tariffHead?.head_code))
        .reduce((sum, i) => sum + itemAmount(i), 0) || num(invoice.arrears_amount);
    const interestArrear = arrearItems
        .filter(i => i.tariffHead?.head_code === 'INT')
        .reduce((sum, i) => sum + itemAmount(i), 0);
    const lessAdjustments = Math.abs(num(invoice.adjustment_amount)) + num(invoice.discount_amount);
    const total = chargeSubtotal + taxTotal + currentInterest;
    const netPayable = num(invoice.net_payable);

    const formatCurrency = (amount) =>
        num(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatDate = (dateStr) => formatIstDate(dateStr, '');

    const numberToWords = (value) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const three = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + three(n % 100) : '');
        };
        const abs = Math.abs(Math.round(num(value)));
        if (abs === 0) return 'Zero';
        let result = '';
        const crore = Math.floor(abs / 10000000);
        const lakh = Math.floor((abs % 10000000) / 100000);
        const thousand = Math.floor((abs % 100000) / 1000);
        const rest = abs % 1000;
        if (crore) result += three(crore) + ' Crore ';
        if (lakh) result += three(lakh) + ' Lakh ';
        if (thousand) result += three(thousand) + ' Thousand ';
        if (rest) result += three(rest);
        return result.trim();
    };

    const memberName = member.name
        || (entity.entity_type === 'I'
            ? `${entity.first_name || ''} ${entity.last_name || ''}`.trim()
            : entity.company_name)
        || '';

    const unitLabel = `${flat.wing?.title ? `${flat.wing.title}/` : ''}${flat.flat_number || member.flat_no || ''}`;
    const area = flat.carpet_area || member.flat_area;
    const documentNumber = invoice.document_number || `${series.prefix || ''}${invoice.invoice_id || ''}`;

    const periodText = invoice.period_start
        ? `${formatDate(invoice.period_start)} To ${formatDate(invoice.period_end)}`
        : '';

    // Formatted Bill Note (stored as HTML by the editor); honours Show Invoice Note.
    const noteHtml = config.show_invoice_note === 1 && hasNoteContent(config.invoice_notes) ? config.invoice_notes : '';

    // Latest receipt allocation for the counterfoil
    const allocation = invoice.receiptInvoiceAllocations?.[0] || null;
    const receipt = allocation?.receipt || null;
    // Only show the receipt counterfoil when an actual payment has been allocated
    // to this invoice. No allocation => bill only (no cut line, no receipt block).
    const interestNote = resolveInterestNote(config, currentInterest + interestArrear);
    const showReceipt = !!allocation && config.invoice_template_show_receipt !== 0;

    const societyName = (society.name || '').toUpperCase();

    const SignatureBlock = () => (
        <div style={{ marginTop: '24px', textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: '11px' }}>For {societyName}</div>
            <div style={{ marginTop: '36px', fontWeight: 700, fontSize: '11px' }}>
                Chairman / Hon. Secretary / Treasurer
            </div>
        </div>
    );

    const SocietyHeader = ({ compact = false }) => (
        <div style={{ textAlign: 'center', paddingBottom: compact ? '6px' : '8px' }}>
            <div style={{ fontSize: compact ? '15px' : '18px', fontWeight: 700, letterSpacing: '0.5px' }}>
                {societyName}
            </div>
            {!compact && society.registration_number && (
                <div style={{ fontSize: '10px', marginTop: '2px' }}>Regd. No. {society.registration_number}</div>
            )}
            {societyAddress && (
                <div style={{ fontSize: '10px', marginTop: '2px' }}>{societyAddress}</div>
            )}
        </div>
    );

    return (
        <div
            className={className}
            style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: config.print_font_size ? `${config.print_font_size}px` : '12px', color: '#000', padding: '18px 22px' }}
        >
            {/* ===== BILL ===== */}
            <div style={{ border: '1px solid #000', padding: '14px 16px' }}>
                <SocietyHeader />

                {/* Bill No / Period / Bill Date */}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #000', paddingTop: '6px', fontSize: '11px', fontWeight: 600 }}>
                    <div>Bill No. {documentNumber}</div>
                    <div>Period : {periodText}</div>
                    <div>Bill Date: {formatDate(invoice.bill_date || invoice.invoice_date)}</div>
                </div>

                {/* Flat No / Area */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px', fontWeight: 600 }}>
                    <div>Flat No. : {unitLabel}</div>
                    {area ? <div>Area {area} Sq. Ft.</div> : <div />}
                </div>
                {vehicles && (
                    <div style={{ marginTop: '6px', fontSize: '11px', fontWeight: 600 }}>
                        Vehicles : {vehicles}
                    </div>
                )}

                {/* Member name */}
                <div style={{ marginTop: '6px', fontSize: '11px', fontWeight: 600 }}>
                    Name : {memberName}
                </div>

                {/* Particulars table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '11px' }}>
                    <thead>
                        <tr>
                            <th style={{ border: '1px solid #000', padding: '4px 6px', width: '50px', textAlign: 'center' }}>Sr.No.</th>
                            <th style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'left' }}>Particulars</th>
                            <th style={{ border: '1px solid #000', padding: '4px 6px', width: '120px', textAlign: 'right' }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {chargeItems.map((item, idx) => (
                            <tr key={item.item_id || idx}>
                                <td style={{ border: '1px solid #000', borderTop: 'none', borderBottom: 'none', padding: '3px 6px', textAlign: 'center' }}>{idx + 1}</td>
                                <td style={{ border: '1px solid #000', borderTop: 'none', borderBottom: 'none', padding: '3px 6px' }}>{item.tariffHead?.head_name || item.description || '-'}</td>
                                <td style={{ border: '1px solid #000', borderTop: 'none', borderBottom: 'none', padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(itemAmount(item))}</td>
                            </tr>
                        ))}
                        {chargeItems.length === 0 && (
                            <tr>
                                <td colSpan={3} style={{ border: '1px solid #000', borderTop: 'none', borderBottom: 'none', padding: '8px 6px', textAlign: 'center', color: '#666' }}>No charges</td>
                            </tr>
                        )}
                        {/* Spacer to give the bill body some height */}
                        <tr>
                            <td style={{ borderLeft: '1px solid #000', borderRight: '1px solid #000' }} />
                            <td style={{ borderRight: '1px solid #000' }} />
                            <td style={{ borderRight: '1px solid #000' }} />
                        </tr>
                    </tbody>
                </table>

                {/* Totals block */}
                <table style={{ width: '100%', borderCollapse: 'collapse', borderLeft: '1px solid #000', borderRight: '1px solid #000', borderBottom: '1px solid #000', fontSize: '11px' }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: '3px 6px', fontWeight: 700, width: '55%' }}>INTEREST</td>
                            <td style={{ padding: '3px 8px', textAlign: 'right', color: '#555' }}>...</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(currentInterest)}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '3px 6px', fontWeight: 700 }}>TOTAL</td>
                            <td />
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(total)}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '3px 6px' }}>Additional Previous Dues (Principle Amt)</td>
                            <td style={{ padding: '3px 8px', textAlign: 'right', color: '#555' }}>...</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(principalArrear)}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '3px 6px' }}>Additional Previous Dues (Interest Amt.)</td>
                            <td style={{ padding: '3px 8px', textAlign: 'right', color: '#555' }}>...</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(interestArrear)}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '3px 6px' }}>Less Adjustments</td>
                            <td style={{ padding: '3px 8px', textAlign: 'right', color: '#555' }}>...</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(lessAdjustments)}</td>
                        </tr>
                        <tr style={{ borderTop: '1px solid #000' }}>
                            <td style={{ padding: '4px 6px', fontWeight: 700 }}>Net Payable</td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', color: '#555' }}>...</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(netPayable)}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Words + due date */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '6px' }}>
                    <div style={{ fontSize: '11px', fontStyle: 'italic' }}>Rupees {numberToWords(netPayable)} Only</div>
                    <div style={{ fontSize: '10px' }}>E. &amp; O.E.</div>
                </div>
                <div style={{ fontSize: '11px', fontWeight: 700, marginTop: '2px' }}>
                    Due Date : {formatDate(invoice.due_date)}
                </div>

                {/* Interest note — configured under Functional Settings. */}
                {interestNote && (
                    <div style={{ fontSize: '9.5px', marginTop: '10px', lineHeight: 1.4 }}>
                        <span style={{ fontWeight: 700 }}>Interest Note : </span>{interestNote}
                    </div>
                )}

                {/* Note */}
                {noteHtml && (
                    <div style={{ fontSize: '9.5px', marginTop: '10px', lineHeight: 1.4 }}>
                        <span style={{ fontWeight: 700 }}>Note : </span><NoteHtml html={noteHtml} style={{ display: 'inline-block', verticalAlign: 'top' }} />
                    </div>
                )}

                <SignatureBlock />
            </div>

            {/* ===== Cut line ===== */}
            {showReceipt && (
                <div style={{ borderTop: '1px dashed #000', margin: '16px 0', position: 'relative' }}>
                    <span style={{ position: 'absolute', top: '-8px', left: 0, fontSize: '10px', background: '#fff', paddingRight: '4px' }}>✂</span>
                </div>
            )}

            {/* ===== RECEIPT counterfoil ===== */}
            {showReceipt && (
                <div style={{ border: '1px solid #000', padding: '14px 16px' }}>
                    <SocietyHeader compact />
                    <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '13px', letterSpacing: '4px', margin: '6px 0' }}>
                        R E C E I P T
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700 }}>
                        <div>Receipt No : {receipt?.receipt_number || '—'}</div>
                        <div>Receipt Date : {receipt?.payment_date ? formatDate(receipt.payment_date) : '—'}</div>
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '12px', lineHeight: 1.6 }}>
                        Received with thanks from ({unitLabel}) a sum of Rs. {formatCurrency(allocation?.amount || netPayable)}/-
                        (Rupees {numberToWords(allocation?.amount || netPayable)} Only)
                        {receipt?.payment_mode ? ` By ${receipt.payment_mode}` : ''}
                        {receipt?.reference_no ? ` No. ${receipt.reference_no}` : ''}
                        {receipt?.payment_date ? ` Dated ${formatDate(receipt.payment_date)}` : ''}
                        {receipt?.bank_name ? ` drawn on ${receipt.bank_name}` : ''}
                        {` Against Bill No. ${documentNumber} Dated ${formatDate(invoice.bill_date || invoice.invoice_date)}`}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '24px' }}>
                        <div style={{ fontSize: '9px' }}>*Receipt subject to realisation of cheque</div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700, fontSize: '11px' }}>For {societyName}</div>
                            <div style={{ marginTop: '28px', fontWeight: 700, fontSize: '11px' }}>
                                Chairman / Hon. Secretary / Treasurer
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {totalPages > 1 && (
                <div style={{ textAlign: 'right', fontSize: '9px', color: '#888', marginTop: '6px' }}>
                    Page {pageIdx + 1} of {totalPages}
                </div>
            )}
        </div>
    );
};

export default InvoiceTemplateClassic;
